<script setup lang="ts">
import {computed, ref} from "vue";
import {useConfirm} from "primevue/useconfirm";
import {useI18n} from "vue-i18n";
import {
  JUDGMENT_COLOR_CHOICES,
  deleteJudgmentOption,
  judgmentOptions,
  kdm,
  updateJudgmentOption
} from "@/store/kdm";

const { t } = useI18n();
const confirm = useConfirm();

const props = defineProps<{
  index: number;
  judgmentId: string;
}>();

const removing = ref(false);

// 8 preset colors for quick selection
const presetColors = [
  '#4CAF50', // Green
  '#8BC34A', // Light Green
  '#FFEB3B', // Yellow
  '#FF9800', // Orange
  '#F44336', // Red
  '#9C27B0', // Purple
  '#2196F3', // Blue
  '#607D8B', // Gray
];

const currentJudgment = computed(() => judgmentOptions.value.get(props.judgmentId));
const isDisabled = computed(() => !currentJudgment.value);

const title = computed({
  get: () => currentJudgment.value?.title ?? '',
  set: value => updateJudgmentOption(props.judgmentId, { title: value }),
});

const colorHex = computed({
  get: () => {
    const value = currentJudgment.value?.color ?? '';
    if (/^#[0-9a-fA-F]{6}$/.test(value)) {
      return value.toUpperCase();
    }
    return JUDGMENT_COLOR_CHOICES[JUDGMENT_COLOR_CHOICES.length - 1];
  },
  set: value => {
    if (typeof value !== 'string') {
      return;
    }
    const sanitized = value.startsWith('#') ? value : `#${value}`;
    const normalized = sanitized.toUpperCase();
    if (/^#[0-9A-F]{6}$/.test(normalized)) {
      updateJudgmentOption(props.judgmentId, { color: normalized });
    }
  },
});

function selectPresetColor(color: string) {
  if (!isDisabled.value) {
    colorHex.value = color;
  }
}

function onColorInput(event: Event) {
  const target = event.target as HTMLInputElement | null;
  if (!target?.value) {
    return;
  }
  colorHex.value = target.value;
}

function removeOption(): void {
  const current = currentJudgment.value;
  if (!current) {
    return;
  }

  const matrixData = kdm.value?.field_matrix_data || '';
  const needsConfirmation = typeof matrixData === 'string' && matrixData.includes(String(props.judgmentId));

  const executeRemoval = async () => {
    removing.value = true;
    try {
      await deleteJudgmentOption(current);
    } finally {
      removing.value = false;
    }
  };

  if (needsConfirmation) {
    confirm.require({
      message: 'Cette option est utilisée au moins une fois dans la matrice. Veuillez confirmer.',
      header: 'Confirmation',
      rejectProps: {
        label: 'ANNULER',
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: 'OK',
      },
      accept: executeRemoval,
    });
  } else {
    executeRemoval().catch(error => console.error('Failed to delete judgment option', error));
  }
}
</script>

<template>
  <div class="kdm__judgment-option" :class="{ removing }">
    <label class="visually-hidden" :for="`judgment-title-${index}`">
      {{ t('epb.kdm.judgment.option') }} {{ index + 1 }}
    </label>
    <input
      :id="`judgment-title-${index}`"
      type="text"
      class="form-control form-control-sm"
      :placeholder="t('epb.kdm.judgment.option') + ' ' + (index + 1)"
      v-model="title"
      :disabled="isDisabled"
    >

    <div class="kdm__color-select">
      <div class="kdm__color-presets">
        <button
          v-for="color in presetColors"
          :key="color"
          type="button"
          class="kdm__color-swatch"
          :class="{ 'kdm__color-swatch--selected': colorHex === color }"
          :style="{ backgroundColor: color }"
          :disabled="isDisabled"
          :title="color"
          @click="selectPresetColor(color)"
        ></button>
      </div>
      <input
        class="kdm__color-picker"
        type="color"
        :value="colorHex"
        :disabled="isDisabled"
        @input="onColorInput"
        :title="t('epb.kdm.judgment.custom_color')"
      >
    </div>

    <button class="btn btn-outline-danger btn-sm" type="button" @click="removeOption" :disabled="removing">
      <i class="bi bi-trash"></i>
    </button>
  </div>
</template>

<style scoped>
.kdm__judgment-option {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
  transition: opacity 0.2s ease;

  &.removing {
    opacity: 0.5;
    pointer-events: none;
  }

  > input[type="text"] {
    flex: 1 1 180px;
    min-width: 120px;
  }
}

.kdm__color-select {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.kdm__color-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.kdm__color-swatch {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: transform 0.1s ease, border-color 0.1s ease;

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--selected {
    border-color: #212529;
  }
}

.kdm__color-picker {
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;

  &::-webkit-color-swatch-wrapper {
    padding: 2px;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 2px;
  }
}
</style>
